'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Button from '@/components/ui/Button';

interface TrainerVideo {
    id: number;
    name: string;
    videoUrl: string;
    thumbnail: string;
}

interface TrainerThumbnailGalleryProps {
    trainers: TrainerVideo[];
    activeTrainer: number;
    onSelect: (index: number) => void;
    className?: string;
}

const getYoutubeId = (url: string): string | null => {
    const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
    );
    return match ? match[1] : null;
};

const TrainerThumbnailGallery: React.FC<TrainerThumbnailGalleryProps> = ({
    trainers,
    activeTrainer,
    onSelect,
    className = '',
}) => {
    return (
        <div className={`flex gap-3.75 sm:gap-4.5 justify-start ${className}`}>
            {trainers.map((trainer, index) => (
                <button
                    key={trainer.id}
                    onClick={() => onSelect(index)}
                    className={`relative w-27.25 h-12.75 sm:w-32 sm:h-15.25 cursor-pointer overflow-hidden transition-all duration-300 group ${activeTrainer === index
                        ? 'ring-1 ring-[#FFB84D] shadow-lg'
                        : ''
                        }`}
                >
                    {/* Thumbnail Image */}
                    <Image
                        src={trainer.thumbnail}
                        alt={trainer.name}
                        title={trainer.name}
                        fill
                        className='object-cover'
                    />

                    {/* Play Icon Overlay on Thumbnail */}
                    <div className='absolute inset-0 flex items-center justify-center transition-all'>
                        <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9.05096 4.7853C9.68512 5.17584 9.68512 6.09773 9.05095 6.48827L1.5243 11.1235C0.858038 11.5338 -7.97161e-05 11.0545 -7.96819e-05 10.272L-7.92767e-05 1.00157C-7.92425e-05 0.2191 0.858039 -0.260227 1.5243 0.150083L9.05096 4.7853Z" fill="white" />
                        </svg>
                    </div>
                </button>
            ))}
        </div>
    );
};

const OurTrainers = () => {
    const [activeTrainer, setActiveTrainer] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showControls, setShowControls] = useState(false);

    const videoRef = useRef<HTMLVideoElement>(null);

    // Trainer video data - replace with actual video URLs
    const trainers: TrainerVideo[] = [
        {
            id: 1,
            name: 'Formateur Principal',
            videoUrl: 'https://www.youtube.com/watch?v=Oi_sweaa0EE',
            thumbnail: '/assets/icons/video-thumbnail.svg'
        },
        {
            id: 2,
            name: 'Formateur 2',
            videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
            thumbnail: '/assets/icons/video-thumbnail-1.png'
        },
        {
            id: 3,
            name: 'Formateur 3',
            videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
            thumbnail: '/assets/icons/video-thumbnail-1.png'
        }
    ];

    const current = trainers[activeTrainer];
    const youtubeId = getYoutubeId(current.videoUrl);

    const handlePlayVideo = () => {
        if (youtubeId) {
            // YouTube: swap the poster for the embed, autoplay handled by the iframe
            setIsPlaying(true);
            return;
        }

        videoRef.current?.play().catch(() => {
            // play() can reject if the element is swapped out mid-request
        });
        setShowControls(true);
        setIsPlaying(true);
    };

    const handleVideoEnded = () => {
        setIsPlaying(false);
    };

    const handleVideoPlay = () => {
        setIsPlaying(true);
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
    };

    const handleTrainerChange = (index: number) => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.load();
        }
        setActiveTrainer(index);
        setIsPlaying(false);
        setShowControls(false);
    };

    return (
        <div className='w-full px-4 py-10.5 sm:py-15 max-w-300 mx-auto'>
            {/* Header */}
            <h2 className="font-red-hat text-[#1B365E] text-[30px] md:text-[40px] leading-10 md:leading-13.25 font-light tracking-[0.3px] text-center">
                Notre force : vos formateurs !
            </h2>
            <p className='mt-4 sm:mt-3.5 text-center text-[#40BFDE] text-[20px] leading-7.5 font-medium tracking-[0.2px]'>
                Tous les formateurs sont sélectionnés avec soin par leur expertise terrain et leur capacité pédagogique.
            </p>

            {/* Main Content Card */}
            <div className='mt-8 md:mt-12.5'>
                <div className='flex items-center lg:flex-row flex-col gap-4 sm:gap-5.5'>
                    {/* Trainer Video Section */}
                    <div className='w-full lg:min-w-126.75 lg:w-126.75 relative h-46.5 sm:h-[267.46px]'>
                        {youtubeId ? (
                            isPlaying ? (
                                <iframe
                                    key={current.id}
                                    className='w-full h-full'
                                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                                    title={current.name}
                                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                    allowFullScreen
                                />
                            ) : (
                                <Image
                                    src={current.thumbnail}
                                    alt={current.name}
                                    fill
                                    className='object-cover'
                                />
                            )
                        ) : (
                            <video
                                ref={videoRef}
                                className='w-full h-full object-cover'
                                controls={showControls}
                                onPlay={handleVideoPlay}
                                onPause={handleVideoPause}
                                onEnded={handleVideoEnded}
                                poster={current.thumbnail}
                            >
                                <source src={current.videoUrl} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )}

                        {/* Play Button Overlay */}
                        {!isPlaying && (
                            <div
                                className='absolute inset-0 flex items-center justify-center cursor-pointer transition-all'
                                onClick={handlePlayVideo}
                            >
                                <div className='w-20 h-20 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300'>
                                    <svg width="41" height="46" viewBox="0 0 41 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M38.8029 19.4806C41.4695 21.0202 41.4695 24.8693 38.8029 26.4089L5.99999 45.3476C3.33332 46.8872 -1.37121e-05 44.9627 -1.35775e-05 41.8835L-1.19219e-05 4.00601C-1.17873e-05 0.926804 3.33332 -0.997699 5.99999 0.541902L38.8029 19.4806Z" fill="white" fillOpacity="0.8" />
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='w-full lg:hidden'>
                        <TrainerThumbnailGallery
                            trainers={trainers}
                            activeTrainer={activeTrainer}
                            onSelect={handleTrainerChange}
                        />
                    </div>

                    {/* Content Section */}
                    <div className='lg:max-w-171.25 w-full mt-4 sm:mt-0 text-center sm:text-left'>
                        <p className='text-[#1D1D1D] text-[14px] leading-5 font-normal tracking-[0.1px]'>
                            Chez Spot Formations, nous sommes dans un <strong>processus d&apos;amélioration continue</strong>, et nous sommes
                            convaincus que la <strong>qualité d&apos;une formation</strong> repose avant tout sur <strong>l&apos;expertise de ses formateurs</strong>. C&apos;est
                            pourquoi nous avons réuni une équipe de professionnels chevronnés, experts dans leur domaine, dotés
                            d&apos;une solide expérience pratique.
                        </p>
                        <br />
                        <p className='text-[#1D1D1D] text-[14px] leading-5 font-normal tracking-[0.1px]'>
                            Au-delà de la transmission de connaissances, <strong>ils vous offrent un accompagnement personnalisé</strong>, un
                            coaching individualisé et un soutien constant tout au long de votre parcours d&apos;apprentissage, vous
                            permettant ainsi de développer pleinement votre potentiel et d&apos;atteindre vos objectifs professionnels.
                        </p>
                        <div className='mt-8'>
                            <Button
                                variant="outline-gradient"
                                className='w-full md:w-auto'
                                onClick={() => { window.location.href = '/formations' }}
                            >
                                Je souhaite afficher mes formations
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Video Thumbnail Gallery (desktop) */}
                <div className='hidden lg:block mt-8'>
                    <TrainerThumbnailGallery
                        trainers={trainers}
                        activeTrainer={activeTrainer}
                        onSelect={handleTrainerChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default OurTrainers;